<template>
  <div class="admin-login-page">
    <aside class="login-brand">
      <div class="brand-inner">
        <div class="brand-badge">Boris Bags</div>
        <h1>商城管理后台</h1>
        <p>产品目录内容统一在此管理。</p>
        <ul class="brand-features">
          <li>商品与分类管理</li>
          <li>目录与页面配置</li>
        </ul>
      </div>
      <div class="brand-pattern" aria-hidden="true"></div>
    </aside>

    <main class="login-main">
      <div class="login-card">
        <div class="login-card-head">
          <div class="login-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M16 11V7a4 4 0 00-8 0v4M5 11h14v10H5V11z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <h2>管理员登录</h2>
          <p>请使用后台账号登录以继续操作</p>
        </div>

        <form class="login-form" @submit.prevent="handleSubmit">
          <div class="form-field">
            <label for="username">账号</label>
            <input
              id="username"
              v-model.trim="form.username"
              type="text"
              name="username"
              autocomplete="username"
              placeholder="请输入管理员账号"
              :disabled="loading"
            />
          </div>

          <div class="form-field">
            <label for="password">密码</label>
            <div class="password-wrap">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                autocomplete="current-password"
                placeholder="请输入密码"
                :disabled="loading"
              />
              <button
                type="button"
                class="toggle-pwd"
                :aria-label="showPassword ? '隐藏密码' : '显示密码'"
                @click="showPassword = !showPassword"
              >
                {{ showPassword ? '隐藏' : '显示' }}
              </button>
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>

          <button type="submit" class="btn-login" :disabled="loading">
            <span v-if="loading" class="btn-spinner"></span>
            {{ loading ? '登录中…' : '登 录' }}
          </button>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import { login } from '@/api/auth'
import { isAdminLoggedIn, setAuth } from '@/utils/adminAuth'

export default {
  name: 'AdminLogin',
  data() {
    return {
      form: {
        username: '',
        password: ''
      },
      showPassword: false,
      remember: true,
      loading: false,
      errorMessage: ''
    }
  },
  computed: {
    year() {
      return new Date().getFullYear()
    }
  },
  mounted() {
    if (isAdminLoggedIn()) {
      this.$router.replace('/admin/catalogue')
    }
  },
  methods: {
    async handleSubmit() {
      this.errorMessage = ''
      if (!this.form.username || !this.form.password) {
        this.errorMessage = '请输入账号和密码'
        return
      }

      this.loading = true
      try {
        const res = await login(this.form.username, this.form.password)
        if (res.code !== 0 || !res.data?.token) {
          this.errorMessage = res.message || '登录失败，请重试'
          return
        }
        setAuth(res.data.token, res.data.user)
        const redirect = this.$route.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/admin')
            ? redirect
            : '/admin/catalogue'
        await this.$router.replace(target)
      } catch (err) {
        const msg = err.message || ''
        if (msg.includes('Cannot POST') || msg.includes('<!DOCTYPE')) {
          this.errorMessage =
            '无法连接后端 API。请确认已在 backend 目录运行 python app.py（端口 5000），并重启 npm run serve。'
        } else {
          this.errorMessage = msg || '网络异常，请确认后端服务已启动'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.admin-login-page {
  min-height: 100vh;
  display: flex;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: #f4f6f9;
}

.login-brand {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background: linear-gradient(145deg, #0f172a 0%, #1e3a5f 45%, #0d9488 100%);
  color: #fff;
  overflow: hidden;
}

.brand-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image: radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px),
    radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px);
  background-size: 48px 48px;
}

.brand-inner {
  position: relative;
  z-index: 1;
  max-width: 420px;
}

.brand-badge {
  display: inline-block;
  padding: 6px 14px;
  margin-bottom: 24px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  backdrop-filter: blur(8px);
}

.login-brand h1 {
  margin: 0 0 12px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.25;
}

.login-brand p {
  margin: 0 0 28px;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
}

.brand-features {
  margin: 0;
  padding: 0;
  list-style: none;
}

.brand-features li {
  position: relative;
  padding: 10px 0 10px 28px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.brand-features li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.25);
}

.login-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px 24px;
}

.login-card {
  width: 100%;
  max-width: 400px;
  padding: 40px 36px 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06);
}

.login-card-head {
  text-align: center;
  margin-bottom: 32px;
}

.login-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-radius: 14px;
  color: #0d9488;
}

.login-icon svg {
  width: 28px;
  height: 28px;
}

.login-card-head h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.login-card-head p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-field input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  font-size: 15px;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-field input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
  background: #fff;
}

.form-field input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.password-wrap {
  position: relative;
}

.password-wrap input {
  padding-right: 64px;
}

.toggle-pwd {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px 8px;
  font-size: 12px;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
}

.toggle-pwd:hover {
  color: #0d9488;
}

.remember-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  user-select: none;
}

.form-error {
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
}

.btn-login {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #0d9488, #0f766e);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
}

.btn-login:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-1px);
}

.btn-login:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.login-hint {
  margin: 20px 0 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  line-height: 1.5;
}

.login-footer {
  margin-top: 32px;
  font-size: 12px;
  color: #94a3b8;
}

@media (max-width: 900px) {
  .admin-login-page {
    flex-direction: column;
  }

  .login-brand {
    padding: 36px 28px;
  }

  .login-brand h1 {
    font-size: 24px;
  }

  .brand-features {
    display: none;
  }
}
</style>
